import { timer } from './helpers'
import { loadScriptBase } from './loadScriptBase'

type LoadingScript = HTMLScriptElement & {
    scriptLoadingState?: 'loading' | 'loaded' | 'error'
    scriptLoadingError?: string
}

export const loadScript = ({
    src,
    crossorigin = null,
    async = true,
    timeoutMs,
    useExisting = false,
    onBeforeFirstLoad,
}: {
    src: string
    crossorigin?: '' | 'anonymous' | 'use-credentials' | null
    async?: boolean
    timeoutMs?: number
    /**
     * It the script with same src already exists, it will be used instead of creating a new one
     */
    useExisting?: boolean
    onBeforeFirstLoad?: (script: HTMLScriptElement) => void
}): Promise<HTMLScriptElement> => {
    if (useExisting) {
        const existingScript = Array.from(document.scripts).find(
            (scriptElement) => scriptElement.src === src,
        ) as LoadingScript
        if (existingScript) {
            if (existingScript.scriptLoadingState === 'loaded') {
                return Promise.resolve(existingScript)
            }

            if (existingScript.scriptLoadingState === 'error') {
                return Promise.reject(
                    new Error(
                        `Script ${src} has failed state: ${existingScript.scriptLoadingError || 'unknown error'}`,
                    ),
                )
            }

            return new Promise((resolve, reject) => {
                existingScript.addEventListener('load', () => {
                    resolve(existingScript)
                    existingScript.scriptLoadingState = 'loaded'
                })
                existingScript.addEventListener('error', (errorEvent) => {
                    const msg = errorEvent.message || (errorEvent.error ? String(errorEvent.error) : 'unknown error')
                    reject(new Error(`Script ${src} has failed state: ${msg}`))
                    existingScript.scriptLoadingState = 'error'
                })
            })
        }
    }

    const abortController = new AbortController()
    if (timeoutMs) {
        timer(timeoutMs).then(() => {
            abortController.abort()
        })
    }

    let loadingScript: LoadingScript
    return loadScriptBase({
        document,
        src,
        abortSignal: abortController.signal,
        onBeforeLoad: (script: LoadingScript) => {
            loadingScript = script as LoadingScript
            script.async = async
            script.scriptLoadingState = 'loading'
            script.crossOrigin = crossorigin
            onBeforeFirstLoad?.(script)
        },
    })
        .then((script: LoadingScript) => {
            script.scriptLoadingState = 'loaded'
            return script
        })
        .catch((error) => {
            loadingScript.scriptLoadingState = 'error'
            loadingScript.scriptLoadingError = error ? String(error) : undefined

            throw error
        })
}
